import { inject, Injectable, type OnDestroy } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import type { InvoicePollResponse } from '../models/invoice-poll-response';

@Injectable({ providedIn: 'root' })
export class InvoicePollingService implements OnDestroy {
	private connection: signalR.HubConnection;
	private connectionPromise: Promise<void>;
	private subscribePromise: Promise<void> = Promise.resolve();
	private invoiceUpdates$ = new Subject<InvoicePollResponse>();
	public readonly updates$ = this.invoiceUpdates$.asObservable();
	private readonly baseUrl = environment.portalApi.uri;
	private readonly authService = inject(AuthService);

	public constructor() {
		this.connection = new signalR.HubConnectionBuilder()
			.withUrl(`${this.baseUrl}/hubs/invoices`, {
				accessTokenFactory: () =>
					firstValueFrom(this.authService.generateApiToken()).then(
						(result) => result.accessToken
					)
			})
			.withAutomaticReconnect()
			.build();

		this.connection.on('InvoicesUpdated', (result: InvoicePollResponse) => {
			this.invoiceUpdates$.next(result);
		});

		this.connectionPromise = this.startConnection();
	}

	public subscribe(parentCompany: string): Promise<void> {
		this.subscribePromise = this.subscribePromise
			.then(() => this.connectionPromise)
			.then(() => this.connection.invoke('Subscribe', parentCompany));
		return this.subscribePromise;
	}

	public ngOnDestroy(): void {
		void this.connection.stop();
	}

	private async startConnection(): Promise<void> {
		try {
			await this.connection.start();
		} catch (err) {
			console.error('[SignalR] Invoice polling connection failed:', err);
		}
	}
}
